<template>
  <div class="schedule">
    <div
      class="timeline-view hidden-mobile"
      :class="{
        'zoom-1x': selectedZoomLevel === 1,
        'zoom-2x': selectedZoomLevel === 2,
        'drawer-open': showForecast,
      }"
    >
      <statistics-overlay ref="statsOverlay"></statistics-overlay>
      <ScheduleDayGrid
        :selectedDate="selectedDate"
        :zoomLevel="selectedZoomLevel"
      />

      <div class="meta-rows">
        <div class="row controls-row">
          <div class="viewport-toggle" style="width:320px; height: 40px;">
            <div class="control-button-toggle">
              <a
                @click="setGrouping('CIRCLE')"
                class="control-button"
                :class="{ 'is-active': groupBy === 'CIRCLE' }"
                >Kredse</a
              >
              <a
                @click="setGrouping('EMPLOYEE')"
                class="control-button"
                :class="{ 'is-active': groupBy === 'EMPLOYEE' }"
                >Medarbejdere</a
              >
            </div>
          </div>
          <div class="viewport-divider"></div>
          <div class="viewport-controls selector-control">
            <GroupDropdown
              :sourceList="filteredGroups"
              @selectionChanged="filterGroups"
              :keyPropertyName="(o) => o.id"
              :displayPropertyName="(o) => o.title"
              :selectedValue="groupFilter"
              :width="200"
            />
          </div>
          <div class="viewport-controls selector-control" v-if="isAdmin">
            <employee-selector
              placeholder="Find gruppe..."
              @selection-changed="viewAs"
              :employees="employees"
              :currentUser="currentUser"
              :isAdmin="isAdmin"
            >
            </employee-selector>
          </div>
          <div class="viewport-controls selector-control" v-if="isAdmin">
            <RegionDropdown
              :sourceList="regions"
              @selectionChanged="filterRegions"
              :keyPropertyName="(o) => o.id"
              :displayPropertyName="(o) => o.name"
              :selectedValue="regionFilter"
              v-bind:value="regionFilter"
              :width="200"
            />
          </div>
          <div class="viewport-controls" v-if="isReplacement">
            <b-switch
              v-model="showAllReplacements"
              type="is-primary"
              size="is-medium"
              >Vis alle afløsere:
              {{ showAllReplacements ? "Ja" : "Nej" }}</b-switch
            >
          </div>
          <div
            class="viewport-toggle"
            style="width:200px; height:42px; padding:0; display:flex;"
            v-if="hideEmployees && hideEmployees.length > 0"
          >
            <div
              class="control-button-toggle"
              style="padding:0px; cursor:pointer; font-size:14px; display:flex;"
            >
              <a
                style="cursor:pointer; align-self:center;"
                @click="clearHiddenEmployees()"
                :class="{ 'is-active': groupBy === 'EMPLOYEE' }"
              >
                Vis skjulte ins.
              </a>
            </div>
          </div>
          <div class="viewport-navigation">
            <div class="control-button-toggle control-button-toggle-zoom">
              <a
                tooltiptext
                tabindex="0"
                v-on:click="zoomOut(10)"
                class="control-icon-button zoom-button control-button"
                :class="{ 'is-active': canZoomOut }"
              >
                <span class>
                  <i data-icon="zoom-out" class="icon"></i>
                </span>
              </a>
              <a
                tooltiptext
                tabindex="0"
                v-on:click="zoomIn(6)"
                class="control-icon-button zoom-button control-button"
                :class="{ 'is-active': canZoomIn }"
              >
                <span class>
                  <i data-icon="zoom-in" class="icon"></i>
                </span>
              </a>
            </div>

            <div class="control-button-toggle">
              <schedule-day-navigator :selectedDate="selectedDate" @navigate-in-weeks="navigateInWeeks">
              </schedule-day-navigator>
            </div>
          </div>
        </div>

        <div class="row week-row">
          <div class="filter-input">
            <input
              type="text"
              :placeholder="filterInputPlaceholder"
              v-model="filter"
            />
          </div>
          <div class="row-drawer drawer-label">
            <div class="drawer-inner" @click="openStatistics">
              {{ forecastTypeTitle() }}
            </div>
          </div>

          <ScheduleWeekGrid
            :selectedDate="selectedDate"
            :zoomLevel="selectedZoomLevel"
            :overAllocations="filteredOverAlloccations"
          />
        </div>
      </div>

      <div class="schedule-rows">
        <div
          class="project-rows"
          v-if="filteredCircles[0] && groupBy === 'CIRCLE'"
        >
          <schedule-row
            v-for="circle of filteredCircles"
            v-bind:key="circle.id"
            :row-data="circle"
            row-data-key="id"
            :subrow-data="allocations.filter((q) => q.circleId === circle.id)"
            subrow-key="id"
            :color="circle.colorCode"
            :viewStartDate="viewStartDate"
            :zoomLevel="selectedZoomLevel"
            :projectStart="circle.allocationStart"
            :projectEnd="circle.allocationEndInclusive"
            :showForecast="showForecast"
          >
            <template v-slot:title="{ rowData }">
              <h3>{{ rowData.title }}</h3>
            </template>

            <template
              v-slot:expandedRows="{ rowData, subrowData, emptyResourceRows }"
            >
              <ScheduleResourceRow
                v-for="employee of circleEmployees(
                  rowData.id,
                  subrowData,
                  emptyResourceRows
                )"
                v-bind:key="employee.id"
                :projectId="rowData.id"
                :employeeId="`${employee.id}`"
                :role="currentUser.role"
                :title="`${employee.number} - ${employee.name}`"
                :projectColor="rowData.colorCode"
                :showForecast="showForecast"
                :viewStartDate="viewStartDate"
                :zoomLevel="selectedZoomLevel"
                @add-allocation="addAllocation"
                @resource-clicked="circleResourceClicked"
                :mainConfig="{
                  store: subrowData.filter(
                    (q) =>
                      q.employeeId === employee.id ||
                      q.replacementId === employee.id
                  ),
                  idProperty: 'id',
                  widthProperty: 'allocationSpanInDays',
                  startProperty: 'startDate',
                  classTypeProperty: 'allocationType',
                }"
                :alertConfig="{
                  store: findOverallocations(rowData, employee),
                  idProperty: 'id',
                  startProperty: 'date',
                }"
                :otherConfigs="[
                  {
                    store: findEmployeeOffTime(employee),
                    idProperty: 'id',
                    startProperty: 'startDate',
                    widthProperty: 'allocationSpanInDays',
                    classProperty: 'time-off-frame',
                  },
                ]"
              >
                <template v-slot:default="{ dataItem }">
                  <div
                    @click="openSidebar($event, dataItem)"
                    class="side-bar-open"
                  >
                    <span
                      class="assignment-label"
                      style="left: 0px;"
                      v-if="dataItem"
                    >
                      {{ getAllocationShortName(dataItem, employee) }}
                    </span>
                  </div>
                </template>
              </ScheduleResourceRow>
            </template>
          </schedule-row>
        </div>

        <div
          class="project-rows"
          v-if="filteredEmployees[0] && groupBy === 'EMPLOYEE'"
        >
          <schedule-row
            v-for="employee of filteredEmployees"
            v-bind:key="employee.id"
            :row-data="employee"
            :row-type="'EMPLOYEE'"
            row-data-key="id"
            :subrow-data="
              allocations.filter(
                (q) =>
                  q.employeeId === employee.id ||
                  q.replacementId === employee.id
              )
            "
            subrow-key="id"
            @hideEmpolyees="updateHiddenEmployees"
            :view-start-date="viewStartDate"
            :zoom-level="selectedZoomLevel"
            :showForecast="showForecast"
            :overallocations="overallocations"
          >
            <template v-slot:title="{ rowData }">
              <h3>{{ rowData.number }} - {{ employee.name }}</h3>
              <div v-if="rowData.role === 'Replacement'">
                <span class="row-info-sub">Afløser</span>
              </div>
            </template>

            <template v-slot:cell="{ rowData, subrowItem }">
              {{ getAllocationShortName(subrowItem, rowData) }}
            </template>

            <template
              v-slot:expandedRows="{ rowData, subrowData, emptyResourceRows }"
            >
              <ScheduleResourceRow
                v-for="circle of employeeCircles(
                  rowData,
                  subrowData,
                  emptyResourceRows
                )"
                :key="circle.id"
                :projectId="circle.id"
                :employeeId="`${rowData.id}`"
                :role="currentUser.role"
                :title="`${circle.title}`"
                :projectColor="circle.colorCode"
                :showForecast="showForecast"
                :viewStartDate="viewStartDate"
                :zoomLevel="selectedZoomLevel"
                @add-allocation="addAllocation"
                @resource-clicked="employeeResourceClicked"
                @hideEmpolyees="updateHiddenEmployees"
                :mainConfig="{
                  store: subrowData.filter((q) => q.circleId === circle.id),
                  idProperty: 'id',
                  widthProperty: 'allocationSpanInDays',
                  startProperty: 'startDate',
                  classTypeProperty: 'allocationType',
                }"
                :otherConfigs="[
                  {
                    store: findEmployeeOffTime(employee),
                    idProperty: 'id',
                    startProperty: 'startDate',
                    widthProperty: 'allocationSpanInDays',
                    classProperty: 'time-off-frame',
                  },
                ]"
              >
                <template v-slot:default="{ dataItem }">
                  <div
                    @click="openSidebar($event, dataItem)"
                    class="side-bar-open"
                  >
                    <span
                      class="assignment-label"
                      style="left: 0px;"
                      v-if="dataItem"
                    >
                      {{ getAllocationShortName(dataItem, employee) }}
                    </span>
                  </div>
                </template>
              </ScheduleResourceRow>
            </template>

            <template
              v-slot:expandedContent="{
                /*rowData, addResource*/
              }"
            >
              <TaskDropdown
                :sourceList="circles"
                @selectionChanged="addCircle"
                :keyPropertyName="(o) => o.id"
                :displayPropertyName="(o) => o.title"
              />
            </template>
          </schedule-row>
        </div>
      </div>
    </div>
    <b-loading
      :is-full-page="isFullPage"
      :active.sync="isLoading"
      :can-cancel="true"
    ></b-loading>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import forecastTypes from "./schedule/constants/forecastTypes";
import ScheduleDayGrid from "./schedule/ScheduleDayGrid.vue";
import ScheduleWeekGrid from "./schedule/ScheduleWeekGrid.vue";
import ScheduleRow from "./schedule/ScheduleRow.vue";
import GroupDropdown from "./schedule/controls/GroupDropdown.vue";
import RegionDropdown from "./schedule/controls/RegionDropdown.vue";
import TaskDropdown from "./schedule/controls/TaskDropdown.vue";
import ScheduleResourceRow from "./schedule/ScheduleResourceRow.vue";
import ScheduleDayNavigator from "./schedule/controls/DayNavigator.vue";
import StatisticsOverlay from "./schedule/StatisticsOverlay.vue";
import EmployeeSelector from "../scheduler/schedule/controls/EmployeeSelector.vue";

export default {
  name: "Schedule",
  components: {
    ScheduleDayGrid,
    ScheduleWeekGrid,
    ScheduleRow,
    GroupDropdown,
    RegionDropdown,
    TaskDropdown,
    ScheduleResourceRow,
    ScheduleDayNavigator,
    StatisticsOverlay,
    EmployeeSelector,
  },
  data: () => ({
    filter: "",
    forecastType: "FUTURE_PLANNED",
    groupBy: "EMPLOYEE",
    groupFilter: null,
    regionFilter: null,
    addResource: null,
    selectedDate: moment
      .utc()
      .startOf("day")
      .toDate(),
    selectedZoomLevel: 1,
    showForecast: true,
    showAllReplacements: false,
    isFullPage: false,
    isLoading: false,
    emptyCircleRows: [],
    hideEmployees: [],
    overAllocations: [],
  }),
  async mounted() {
    await this.getHolidays();
    if (localStorage.selectedZoomLevel) {
      this.selectedZoomLevel = parseInt(localStorage.selectedZoomLevel);
    }

    if (localStorage.showForecast) {
      this.showForecast = localStorage.showForecast === "true";
    }

    if (localStorage.groupFilter && localStorage.groupFilter !== "null") {
      this.groupFilter =
        localStorage.groupFilter === "null" ? null : localStorage.groupFilter;
    } else if (this.currentUser.role !== "admin" && this.currentUser.groupId) {
      this.groupFilter = this.currentUser.groupId;
    }

    if (localStorage.employeeFilter) {
      this.filter =
        localStorage.employeeFilter === "null"
          ? ""
          : localStorage.employeeFilter;
      localStorage.employeeFilter = "";
    }

    if (localStorage.regionFilter) {
      this.regionFilter =
        localStorage.regionFilter === "null" ? null : localStorage.regionFilter;
    }

    if (localStorage.hideEmployees && localStorage.hideEmployees !== "null") {
      this.hideEmployees = JSON.parse(localStorage.hideEmployees);
    }
    await this.$store.dispatch("metadata/initialize");

    if (this.groupFilter) {
      await this.getCircleForGroup({ groupId: this.groupFilter });
    }

    this.isLoading = true;
    this.$store
      .dispatch("scheduler/initialize", {
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        teamFilter: this.groupFilter,
        groupBy: this.groupBy,
        forRegion: this.regionFilter,
      })
      .then(() => {
        // We choose not to open the edit dialog on creation
        this.isLoading = false;
      });
    this.loadGroupComments({
      fromDate: this.viewStartDate,
      toDate: this.viewToDate,
      forGroup: this.groupFilter,
    });

    this.showForecast = true;
    await this.loadReplacements();
  },
  beforeDestroy() {
    this.clearSelection();
  },
  methods: {
    ...mapActions("groupComments", ["loadGroupComments"]),
    ...mapActions("entrySidebar", [
      "entrySidebarOpen",
      "entrySidebarOpenAsync",
    ]),
    ...mapActions("employees", ["loadReplacements"]),
    ...mapActions("statisticsStore", ["loadStatistics"]),
    ...mapActions("allocationMoverStore", ["clearSelection"]),
    ...mapActions("holidayStore", ["getHolidays"]),
    ...mapActions("circles", ["getCircleForGroup"]),
    addAllocation(data) {
      // Whole days from midnight, so the 30-day boundary is deterministic
      // regardless of the current time of day. Kept in sync with
      // isOwnEntryBeyond30Days in EntrySidebar.vue: a replacement may only
      // register/edit/delete entries that start MORE than 30 days ahead, so
      // day 30 and closer is locked (e.g. from 6 July the first allowed start
      // date is 6 August).
      const start = moment(data.startDate).startOf("day");
      const today = moment().startOf("day");
      const daysUntilStart = start.diff(today, "days");

      // Replacement's can't add to the calender at the moment
      if (this.isReplacement || this.hasEditRights === false) {
        if (
          !this.isReplacement ||
          daysUntilStart <= 30 ||
          data.allocationType === "WORK"
        ) {
          alert(
            `Det er kun muligt at angive ferie/fri hvor datoen er mere end 30 dage frem i tiden! Du har prøvet at ligge ind ${daysUntilStart} dage frem i tiden.`
          );
          return;
        }
      }

      this.isLoading = true;
      // Create the allocation, save it to the server
      this.$store
        .dispatch("scheduler/addAllocation", {
          allocationType: data.allocationType,
          circleId: data.circleId,
          employeeId: data.employeeId,
          startDate: data.startDate,
          allocationSpanInDays: data.allocationSpanInDays,
          groupId: this.groupFilter,
        })
        .then(() => {
          // We choose not to open the edit dialog on creation
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getAllocationShortName(allocation, employee) {
      const allocationType = allocation.allocationType;
      if (!allocationType) {
        // || (allocation.replacementId === employee.id && employee.role === "Replacement")
        return "ARB";
      }

      let shortName = allocationType;
      if (this.allocationTypesReport) {
        let obj = this.allocationTypesReport.find(
          (o) => o.key === allocationType
        );
        if (obj) {
          if (obj.shortName) {
            shortName = obj.shortName.toUpperCase();
          } else {
            shortName = obj.label.toUpperCase();
          }
        } else if (this.allocationTypes) {
          shortName = this.allocationTypes[allocationType].toUpperCase();
        }
      } else if (this.allocationTypes) {
        shortName = this.allocationTypes[allocationType].toUpperCase();
      }
      if (
        allocation?.circleName &&
        allocation.circleName.toString().includes("Kreds")
      ) {
        return allocation.circleName.replace("Kreds", "").replace(" ", "");
      }
      let result = shortName.substring(0, 3).toUpperCase();

      if (allocation.circleId) {
        const circle = this.circles.find((a) => a.id === allocation.circleId);
        if (circle?.code) {
          return circle.code;
        }
      }

      return result;
    },
    isSick(allocationType) {
      return (
        allocationType === "SICKDAY" ||
        allocationType === "SICKDAYCHILD" ||
        allocationType === "SICKDAYWORK"
      );
    },
    sickReplacementLabel() {
      let obj = this.allocationTypesReport.find(
        (o) => o.key === "SICKREPLACEMENT"
      );
      if (obj) {
        if (obj.shortName) {
          return obj.shortName.toUpperCase();
        } else {
          return obj.label.toUpperCase();
        }
      }
      return "AFL";
    },
    entryUpdate(entry) {
      console.log(`schedule, entry-update: ${entry}`);
    },
    entryDelete(entry) {
      console.log(`schedule, entry-delete: ${entry}`);
    },
    circleResourceClicked(resource) {
      this.entrySidebarOpen({});
    },
    employeeResourceClicked(resource) {
      this.entrySidebarOpen({});
    },
    updateHiddenEmployees() {
      console.log("updateHiddenEmployees");
      if (localStorage.hideEmployees && localStorage.hideEmployees !== "null") {
        this.hideEmployees = JSON.parse(localStorage.hideEmployees);
      }
    },
    clearHiddenEmployees() {
      console.log("clearHiddenEmployees");
      localStorage.hideEmployees = "null";
      this.hideEmployees = [];
      this.updateHiddenEmployees();
    },
    async filterGroups(groupId) {
      this.groupFilter = groupId;
      localStorage.groupFilter = groupId;
      this.isLoading = true;
      this.$store
        .dispatch("scheduler/changePeriod", {
          fromDate: this.viewStartDate,
          toDate: this.viewToDate,
          teamFilter: this.groupFilter,
          groupBy: this.groupBy,
          forRegion: this.regionFilter,
        })
        .then((res) => {
          this.isLoading = false;
        });
      this.loadGroupComments({
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        forGroup: groupId,
      });
      if (this.groupFilter) {
        await this.getCircleForGroup({ groupId: this.groupFilter });
      }
    },
    filterRegions(region) {
      this.regionFilter = region;
      localStorage.regionFilter = region;
      this.isLoading = true;
      this.$store
        .dispatch("scheduler/changePeriod", {
          fromDate: this.viewStartDate,
          toDate: this.viewToDate,
          teamFilter: this.groupFilter,
          groupBy: this.groupBy,
          forRegion: this.regionFilter,
        })
        .then((res) => {
          this.isLoading = false;
        });
    },
    findOverallocations(circle, employee) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.overallocations.byCircle,
          circle.id
        )
      ) {
        return this.overallocations.byCircle[circle.id].filter(
          (q) => q.employeeId === employee.id
        );
      }
      return [];
    },
    findEmployeeOffTime(employee) {
      return this.allocations.filter(
        (q) => q.circleId === null && q.employeeId === employee.id
      );
    },
    forecastTypeTitle() {
      return forecastTypes[this.groupBy][this.forecastType].title(
        moment(this.viewStartDate).format("MMMM")
      );
    },
    async navigateInWeeks(numberOfWeeks) {
      this.selectedDate = moment
        .utc(this.selectedDate)
        .add(numberOfWeeks * 7, "days")
        .toDate();
      await this.loadReplacements();
      this.navigateInWeeksDebounced();
    },
    navigateInWeeksDebounced: _.debounce(function() {
      this.$store.dispatch("scheduler/changePeriod", {
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        teamFilter: this.groupFilter,
        forRegion: this.regionFilter,
      });
      this.loadGroupComments({
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        forGroup: this.groupFilter,
      });
    }, 500),
    addCircle(circle) {
      this.emptyCircleRows.push(circle);
    },
    circleEmployees(circleId, allocations, emptyResourceRows) {
      var compare = (a, b) => {
        if (a.firstName < b.firstName) return -1;
        if (a.firstName > b.firstName) return 1;
        return 0;
      };

      var employeeIds = Array.from(allocations, (o) => o.employeeId);
      employeeIds = employeeIds.concat(
        Array.from(allocations, (o) => o.replacementId)
      );
      employeeIds = employeeIds.concat(emptyResourceRows);

      employeeIds = employeeIds.filter((e) => e);

      // Distinct
      employeeIds = employeeIds.filter(
        (value, index, self) => self.indexOf(value) === index
      );

      let result = Array.from(employeeIds, (id) => this.employeeLookup[id])
        .filter((o) => o)
        .filter(
          (e) =>
            e.circleId === circleId ||
            !this.groupFilter ||
            e.groupId === this.groupFilter ||
            e.role === "Replacement"
        )
        .filter(
          (o) =>
            !this.regionFilter ||
            o.region === "begge" ||
            o.region === this.regionFilter
        )
        .sort(compare);

      //console.log(result);
      return result;
    },
    employeeCircles(employee, allocations, emptyResourceRows) {
      var compare = (a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      };

      var circleIds = Array.from(allocations, (o) => o.circleId);
      circleIds = circleIds.concat(this.emptyCircleRows);

      // Distinct
      circleIds = circleIds.filter(
        (value, index, self) => value && self.indexOf(value) === index
      );
      circleIds = circleIds.filter((o) => o); // Remove undefined

      let circles = Array.from(circleIds, (id) => this.circleLookup[id]).sort(
        compare
      );

      circles = circles.filter((o) => o); // Remove undefined

      if (circles.findIndex((q) => q.title === "Bagvagt") === -1) {
        let circleStandIn = this.circles.find((o) => o.title === "Bagvagt");
        if (circleStandIn) {
          circles.push(circleStandIn);
        }
      }

      if (circles.findIndex((q) => q.id === null) === -1) {
        circles.unshift({
          id: null,
          title: "Ferie/Fri",
          code: "",
          colorCode: "black",
        });
      }

      return circles;
    },
    setGrouping(groupBy) {
      this.groupBy = groupBy;
      this.forecastType = "FUTURE_PLANNED";
      this.$store.dispatch("scheduler/initialize", {
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        teamFilter: this.groupFilter,
        forRegion: this.regionFilter,
        groupBy: groupBy,
      });
      this.loadGroupComments({
        fromDate: this.viewStartDate,
        toDate: this.viewToDate,
        forGroup: this.groupFilter,
      });
    },
    toggleForecastType() {
      let types = Object.getOwnPropertyNames(forecastTypes[this.groupBy]);
      let currentIndex = types.indexOf(this.forecastType);

      if (currentIndex < types.length - 1) {
        this.forecastType = types[currentIndex + 1];
      } else {
        // Return first element
        this.forecastType = types[0];
      }

      // Call forecasting
      this.$store.dispatch("scheduler/updateForecasts", {
        forecastKey: this.forecastType,
      });
    },
    zoomIn() {
      if (this.selectedZoomLevel > 1) {
        this.selectedZoomLevel--;
        this.navigateInWeeks();
      }
    },
    zoomOut() {
      if (this.selectedZoomLevel < 2) {
        this.selectedZoomLevel++;
        this.navigateInWeeks();
      }
    },
    canEdit(entry) {
      const isAdmin = this.isAdmin;
      let groupId = this.groupFilter;
      const userId = this.currentUser.id;

      if (
        groupId &&
        groupId.toLowerCase() === "71e5b9c2-baf0-4332-af46-9c3d5f6ffe73"
      ) {
        if (userId !== entry.employeeId && userId !== entry.replacementId) {
          if (!isAdmin) {
            return false;
          }
        }
      }

      return true;
    },
    async openSidebar($event, entry) {
      if ($event.ctrlKey === true) {
        return;
      }
      if (this.canEdit(entry) === false) {
        return;
      }
      entry.canEdit = true;

      await this.entrySidebarOpenAsync(entry)
        .then((response) => {
          if (response.action === "update") {
            //console.log(response);
            const { model } = response;

            if (model) {
              this.$store
                .dispatch("scheduler/updateAllocation", { allocation: model })
                .then(() => {})
                .catch((error) => {
                  console.log(error);
                })
                .finally(() => {});
            }
          }
          if (response.action === "delete") {
            const { model } = response;

            if (model) {
              this.$store
                .dispatch("scheduler/deleteAllocation", {
                  allocationId: model.id,
                  updateTag: model._UpdateTag,
                })
                .then(() => {})
                .catch((error) => {
                  console.log(error);
                })
                .finally(() => {});
            }
          }
          if (response.action === "split") {
            this.$store.dispatch("scheduler/changePeriod", {
              fromDate: this.viewStartDate,
              toDate: this.viewToDate,
              teamFilter: this.groupFilter,
              forRegion: this.regionFilter,
            });
          }
        })
        .catch((error) => console.error(error));
    },
    async openStatistics() {
      if (this.$refs.statsOverlay.toggle()) {
        await this.loadStatistics({
          groupFilter: this.groupFilter,
          regionFilter: this.regionFilter,
        });
      }
    },
    async viewAs(employeeAndGroup) {
      if (employeeAndGroup && employeeAndGroup.group) {
        this.filterGroups(employeeAndGroup.group.id);
      }
    },
    getStyle(atWorkGroup, assignments, allFree) {
      const colorStyles = {
        yellowUnderAllocation: { background: "#ffffa7" },
        yellowOverAllocation: { background: "#fff200" },
        white: { background: "white" },
        allFree: { background: "red" },
      };
      if (allFree) {
        return colorStyles.allFree;
      }
      if (atWorkGroup < assignments) {
        return colorStyles.yellowUnderAllocation;
      } else if (atWorkGroup > assignments) {
        return colorStyles.yellowOverAllocation;
      } else {
        return colorStyles.white;
      }
    },
  },
  computed: {
    ...mapGetters(["currentUser", "isAdmin", "isReplacement", "hasEditRights"]),
    canZoomIn() {
      return this.selectedZoomLevel === 1;
    },
    canZoomOut() {
      return this.selectedZoomLevel === 2;
    },
    filteredOverAlloccations() {
      let overAllocations = [];
      console.log("running: filteredOverAlloccations");
      if (this.allocations && this.groups && this.groupFilter) {
        var a = moment(this.viewStartDate);
        var b = moment(this.viewToDate);
        const totalDays = b.diff(a, "days") + 1; // =1

        let holidayLookUp = [];

        if (
          this.holidays &&
          this.holidays.length > 0 &&
          (!this.holidayLookUp || this.holidayLookUp.length === 0)
        ) {
          for (const h of this.holidays) {
            holidayLookUp.push({
              dateString: moment(h.date).format("YYYY-MM-DD"),
              allFree: h.state === 1,
            });
          }
        }
        for (let days = 0; days < totalDays; days++) {
          let styles = { background: "white" };
          let result = null;
          const date = moment(a).add(days, "Days");

          const dateString = date.format("YYYY-MM-DD");
          const cDay = moment(dateString);
          const wDay = cDay.isoWeekday();
          const group = this.groups.find((o) => o.id === this.groupFilter);
          const assignments = this.allocations
            .filter(
              (o) =>
                moment(o.startDate).format("YYYY-MM-DD") === dateString ||
                (o.allocationSpanInDays > 1 &&
                  moment(o.startDate) <= cDay &&
                  cDay <= moment(o.endDateInclusive))
            )
            .filter((o) => o.circle && o.circle.showOverallocation);

          // Makes sure that not more than 1 overAllocation for each employee
          let assingmentWithOneEmployeeId = [];
          for (let x of assignments) {
            const alreadyExists = assingmentWithOneEmployeeId.some(
              (y) => x.employeeId === y
            );
            if (!alreadyExists) {
              assingmentWithOneEmployeeId.push(x.employeeId);
            }
          }
          let iconSymbol = false;

          if (assingmentWithOneEmployeeId) {
            const isHoliday = holidayLookUp.find(
              (o) => o.dateString === dateString
            );
            const isAllFree = holidayLookUp.find(
              (o) => o.dateString === dateString && o.allFree
            );
            if (isAllFree) {
              styles = { background: "#b23434", color: "white" };
              result = "Alle har fri";
            } else if (group) {
              if (wDay !== 6 && wDay !== 7) {
                if (isHoliday && group.atWorkHolidayWeekdays > 0) {
                  if (
                    group.atWorkHolidayWeekdays !==
                    assingmentWithOneEmployeeId.length
                  ) {
                    styles = this.getStyle(
                      group.atWorkHolidayWeekdays,
                      assingmentWithOneEmployeeId.length
                    );
                    result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkHolidayWeekdays}`;
                    iconSymbol = "exclamation";
                  }
                } else if (
                  group.atWorkWeekDays !== assingmentWithOneEmployeeId.length &&
                  group.atWorkWeekDays > 0
                ) {
                  styles = this.getStyle(
                    group.atWorkWeekDays,
                    assingmentWithOneEmployeeId.length
                  );
                  result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkWeekDays}`;
                  iconSymbol = "exclamation";
                }
              }
              if (wDay === 6) {
                if (isHoliday && group.atWorkHolidayWeekend > 0) {
                  if (
                    group.atWorkHolidayWeekend !==
                    assingmentWithOneEmployeeId.length
                  ) {
                    styles = this.getStyle(
                      group.atWorkHolidayWeekend,
                      assingmentWithOneEmployeeId.length
                    );
                    result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkHolidayWeekend}`;
                    iconSymbol = "exclamation";
                  }
                } else if (
                  group.atWorkWeekSaturday !==
                    assingmentWithOneEmployeeId.length &&
                  group.atWorkWeekSaturday > 0
                ) {
                  styles = this.getStyle(
                    group.atWorkWeekSaturday,
                    assingmentWithOneEmployeeId.length
                  );
                  result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkWeekSaturday}`;
                  iconSymbol = "exclamation";
                }
              }
              if (wDay === 7) {
                if (isHoliday && group.atWorkHolidayWeekend > 0) {
                  if (
                    group.atWorkHolidayWeekend !==
                    assingmentWithOneEmployeeId.length
                  ) {
                    styles = this.getStyle(
                      group.atWorkHolidayWeekend,
                      assingmentWithOneEmployeeId.length
                    );
                    result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkHolidayWeekend}`;
                    iconSymbol = "exclamation";
                  }
                } else if (
                  group.atWorkWeekSunday !==
                    assingmentWithOneEmployeeId.length &&
                  group.atWorkWeekSunday > 0
                ) {
                  styles = this.getStyle(
                    group.atWorkWeekSunday,
                    assingmentWithOneEmployeeId.length
                  );
                  result = `Der er ${assingmentWithOneEmployeeId.length} på arbejde, men der skal være ${group.atWorkWeekSunday}`;
                  iconSymbol = "exclamation";
                }
              }
            }
          }

          overAllocations.push({
            key: date.format("YYYY-MM-DD"),
            value: result,
            styles: styles,
            icon: iconSymbol,
          });
        }
      }

      return overAllocations;
    },
    filteredGroups() {
      if (this.groups) {
        let groupsByRegion = this.groups
          .filter(
            (o) =>
              !this.regionFilter ||
              o.region === "begge" ||
              o.region === this.regionFilter
          )
          .filter((o) => !o.isRemoved);

        if (groupsByRegion) {
          return groupsByRegion;
        }
      }
      return this.groups;
    },
    filteredCircles() {
      if (this.circles) {
        // Extract all projects team members are working on
        let circlesAllocated = this.allocations
          .filter(
            (o) =>
              !this.groupFilter ||
              o.groupId === null ||
              o.groupId === this.groupFilter ||
              this.employeeLookup[o.employeeId]?.groupId === this.groupFilter ||
              this.employeeLookup[o.employeeId]?.groups.find(
                (f) => f.groupId === this.groupFilter
              ) ||
              (this.isReplacement &&
                (this.currentUser.id === o.replacementId ||
                  this.currentUser.id === o.employeeId))
          )
          .filter(
            (o) =>
              !this.regionFilter ||
              this.employeeLookup[o.employeeId]?.region === "begge" ||
              this.employeeLookup[o.employeeId]?.region === this.regionFilter ||
              this.employeeLookup[o.employeeId]?.groups.find(
                (f) => f.groupId === this.groupFilter
              ) ||
              (this.isReplacement &&
                (this.currentUser.id === o.replacementId ||
                  this.currentUser.id === o.employeeId))
          );

        var circleIds = Array.from(circlesAllocated, (o) => o.circleId);

        // Distinct
        circleIds = circleIds.filter(
          (value, index, self) => value && self.indexOf(value) === index
        );

        var compare = (a, b) => {
          // if (a.clientName + a.title < b.clientName + b.title)
          //   return -1;
          // if (a.clientName + a.title > b.clientName + b.title)
          //   return 1;
          if (a.allocationStart < b.allocationStart) return -1;
          if (a.allocationStart > b.allocationStart) return 1;
          return 0;
        };

        const filterLowerCase = this.filter.toLowerCase();
        var filteredCircles = this.circles
          .filter(
            (o) =>
              (o.title.toLowerCase().includes(filterLowerCase) ||
                o.colorCode.toLowerCase().includes(filterLowerCase)) &&
              (this.groupFilter === null ||
                o.groupId === this.groupFilter ||
                circleIds.indexOf(o.id) > -1)
          )
          .sort(compare);

        // Insert absence project at index 0
        if (filteredCircles.findIndex((q) => q.id === null) === -1) {
          filteredCircles.unshift(this.circles[0]);
        }

        if (filteredCircles) {
          return filteredCircles;
        }
      }

      return this.circles;
    },
    filteredEmployees() {
      let hideEmployees = this.hideEmployees;
      if (this.employees) {
        var compare = (a, b) => {
          let noa = a.orderNumber;
          let nob = b.orderNumber;
          if (a.groups.find((o) => o.groupId === this.groupFilter)) {
            noa = a.groups.find((o) => o.groupId === this.groupFilter)
              .orderNumber;
          }
          if (b.groups.find((o) => o.groupId === this.groupFilter)) {
            nob = b.groups.find((o) => o.groupId === this.groupFilter)
              .orderNumber;
          }

          if (noa < nob) {
            return -1;
          }

          if (noa > nob) {
            return 1;
          }

          if (a.orderNumber < b.orderNumber) return -1;
          if (a.orderNumber > b.orderNumber) return 1;
          return 0;
        };

        const filterLowerCase = this.filter.toLowerCase();

        let filteredEmployees = this.employees
          .filter(
            (o) =>
              (!this.groupFilter ||
                o.groupId === this.groupFilter ||
                o.groups.find((f) => f.groupId === this.groupFilter)) &&
              (!this.regionFilter ||
                o.region === "begge" ||
                o.region === this.regionFilter) &&
              (o.name.toLowerCase().includes(filterLowerCase) ||
                o.number
                  .toString()
                  .toLowerCase()
                  .includes(filterLowerCase))
          )
          .sort(compare);

        if (this.isReplacement) {
          if (this.showAllReplacements) {
            filteredEmployees = filteredEmployees
              .filter(
                (o) =>
                  o.groupId === this.groupFilter ||
                  o.groups.find((f) => f.groupId === this.groupFilter)
              )
              .sort(compare);
          } else {
            filteredEmployees = filteredEmployees
              .filter(
                (o) =>
                  o.id === this.currentUser.id ||
                  o.groups.find((f) => f.groupId === this.groupFilter) ||
                  (o.groupId === this.groupFilter && o.role !== "Replacement")
              )
              .sort(compare);
          }
        }

        if (hideEmployees && hideEmployees.length > 0) {
          filteredEmployees = filteredEmployees.filter(
            (o) => !hideEmployees.find((f) => f.id === o.id)
          );
        }

        if (filteredEmployees) return filteredEmployees;
      }

      return this.employees;
    },
    selectedWeekNumber() {
      return moment.utc(this.selectedDate).week();
    },
    viewStartDate() {
      var startDate = moment.utc(this.selectedDate);
      startDate = startDate.add(-startDate.isoWeekday() + 1, "days");
      return startDate.toDate();
    },
    viewToDate() {
      return moment(this.viewStartDate)
        .add(this.selectedZoomLevel * 8, "weeks")
        .toDate();
    },
    ...mapGetters("scheduler", [
      "circles",
      "circleLookup",
      "allocations",
      "employees",
      "employeeLookup",
      "milestones",
      "teams",
      "overallocations",
      "groups",
    ]),
    ...mapGetters("metadata", [
      "allocationTypesReport",
      "allocationTypes",
      "regions",
    ]),
    ...mapGetters("holidayStore", ["holidays"]),
    ...mapGetters(["currentUser", "hasEditRights"]),
    isAdmin() {
      return this.currentUser.role === "Admin";
    },
    filterInputPlaceholder() {
      switch (this.groupBy) {
        case "CIRCLE":
          return "Filtrér på opgave titel";
        case "EMPLOYEE":
        default:
          return "Filtrér på medarbejder navn eller initialer";
      }
    },
  },
};
</script>

<style lang="scss">
@import "./styles/scheduler";
</style>

<style lang="scss" scoped>
.drawer-inner {
  cursor: pointer;
}

.schedule {
  overflow-y: auto;
}

.button .icon:first-child:last-child {
  margin: 6px 5px 0 -5px;
}
.side-bar-open {
  height: 90%;
  width: 90%;
}
.selector-control {
  max-width: 295px;
  min-width: 200px;
}

@media screen and (max-width: 1480px) {
  .selector-control {
    max-width: 200px;
    min-width: 200px;
  }

  .control-button-toggle-zoom {
    display: none;
  }
}
</style>
